import React, { useState, useRef, useCallback } from 'react';
import {
    Search, Sparkles, Loader2, ImageIcon, Wand2, Download, RefreshCw, Copy, Check, X,
    ArrowLeft, Star, Leaf, Camera, BarChart2, Layers, Eye, Grid, Zap, ChevronRight, Edit3,
    Plus, CheckSquare, Square, MessageSquarePlus, Palette, Trash2, Upload
} from 'lucide-react';
import authService from '../services/AuthService';
import { API_BASE } from '../consts';

const STYLES = [
    { id: 'hero', label: 'Hero Shot', icon: Star, desc: 'Centered product, props, benefits' },
    { id: 'ingredients', label: 'Ingredients', icon: Leaf, desc: 'Raw ingredients scattered (best for grocery/food)' },
    { id: 'lifestyle', label: 'Lifestyle', icon: Camera, desc: 'Product in real-life context' },
    { id: 'infographic', label: 'Infographic', icon: BarChart2, desc: 'Feature callouts with icons' },
    { id: 'comparison', label: 'vs Competitor', icon: Layers, desc: 'Side-by-side comparison' },
    { id: 'texture', label: 'Texture/Macro', icon: Eye, desc: 'Extreme close-up detail' },
    { id: 'flat_lay', label: 'Flat Lay', icon: Grid, desc: 'Top-down editorial style' },
    { id: 'before_after', label: 'Before / After', icon: Zap, desc: 'Transformation story' },
];

// Styles selected by default when the user lands on step 2. Everything else
// (ingredients included) is opt-in since it's not universally relevant.
const DEFAULT_SELECTED = new Set(['hero', 'lifestyle']);

const SIZES = [
    { label: 'Square 1:1', w: 1024, h: 1024 },
    { label: 'Portrait 4:5', w: 1024, h: 1280 },
    { label: 'Story 9:16', w: 1024, h: 1792 },
    { label: 'Banner 3:1', w: 1536, h: 512 },
];

// Hero shots must always land on a true white background. We append this
// clause to whatever prompt is about to be sent (auto-generated OR
// user-edited) so it can't accidentally get dropped. See note to backend:
// this should ideally also be hard-coded server-side, and ideally
// post-processed (bg removal -> flatten to #FFFFFF) for a guarantee.
const HERO_BG_CLAUSE = "The product must be photographed on a pure, seamless white background (#FFFFFF), studio e-commerce lighting, no gradients, no colored backdrop, no props or shadows touching the background — a clean isolated white-background hero shot.";

function withHeroBackground(prompt, styleId) {
    if (styleId !== 'hero') return prompt || '';
    const base = prompt || '';
    if (base.includes('#FFFFFF')) return base; // already enforced, don't duplicate
    return base ? `${base}\n\n${HERO_BG_CLAUSE}` : HERO_BG_CLAUSE;
}

// Cost control: 'high' quality on GPT Image 2 is ~4x medium and ~35x low.
// Only the hero shot (the one people actually judge the listing by) gets
// 'high' by default — everything else runs on 'medium'.
function qualityFor(styleId) {
    return styleId === 'hero' ? 'high' : 'medium';
}

// ─── Step indicator ───────────────────────────────────────────────────────────
const Steps = ({ current }) => {
    const steps = [
        { n: 1, label: 'Product' },
        { n: 2, label: 'Style' },
        { n: 3, label: 'Generate' },
    ];
    return (
        <div className="flex items-center gap-0 mb-10">
            {steps.map((s, i) => (
                <React.Fragment key={s.n}>
                    <div className="flex flex-col items-center">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500
                            ${current >= s.n ? 'bg-gradient-to-br from-purple-500 to-pink-600 text-white shadow-lg shadow-purple-200' : 'bg-gray-100 text-gray-400'}`}>
                            {current > s.n ? '✓' : s.n}
                        </div>
                        <span className={`text-xs mt-1 font-medium transition-colors duration-300 ${current >= s.n ? 'text-purple-600' : 'text-gray-400'}`}>
                            {s.label}
                        </span>
                    </div>
                    {i < steps.length - 1 && (
                        <div className={`flex-1 h-0.5 mb-5 mx-1 transition-all duration-700 ${current > s.n ? 'bg-gradient-to-r from-purple-500 to-pink-600' : 'bg-gray-200'}`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};

// ─── Single generated image card ──────────────────────────────────────────────
const ImageCard = ({ img, meta, onRegenerate, onDownload, onEdit, generating }) => {
    const [editMode, setEditMode] = useState(false);
    const [editPrompt, setEditPrompt] = useState('');
    const [editing, setEditing] = useState(false);

    const handleEdit = async () => {
        if (!editPrompt.trim()) return;
        setEditing(true);
        await onEdit(img, editPrompt.trim());
        setEditing(false);
        setEditMode(false);
        setEditPrompt('');
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden group animate-fadeIn">
            <div className="relative">
                <img src={img.image_url} alt={img.style_name} className="w-full object-cover" />

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
                    <button onClick={() => onDownload(img)} title="Download"
                        className="w-11 h-11 bg-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition">
                        <Download size={18} className="text-gray-800" />
                    </button>
                    <button onClick={() => onRegenerate(img)} disabled={generating} title="Regenerate"
                        className="w-11 h-11 bg-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition disabled:opacity-50">
                        <RefreshCw size={18} className={`text-gray-800 ${generating ? 'animate-spin' : ''}`} />
                    </button>
                    <button onClick={() => setEditMode(e => !e)} title="Edit"
                        className="w-11 h-11 bg-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition">
                        <Edit3 size={18} className="text-gray-800" />
                    </button>
                </div>

                <div className="absolute top-2 left-2">
                    <span className="bg-black/60 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                        {meta?.label || img.style_name}
                    </span>
                </div>
            </div>

            {img.hero_text && (
                <div className="px-4 py-3 border-t border-gray-100">
                    <p className="text-xs font-bold text-purple-600 uppercase tracking-wide">{img.hero_text}</p>
                    {img.sub_text && <p className="text-xs text-gray-400 mt-0.5">{img.sub_text}</p>}
                </div>
            )}

            {editMode && (
                <div className="px-4 py-4 border-t border-purple-100 bg-purple-50 animate-fadeIn">
                    <p className="text-xs font-bold text-purple-700 mb-2 flex items-center gap-1.5">
                        <Edit3 size={11} /> What would you like to change?
                    </p>
                    <textarea
                        value={editPrompt}
                        onChange={e => setEditPrompt(e.target.value)}
                        placeholder="e.g. 'Make the background darker', 'Add more lemons', 'Change lighting to golden hour'..."
                        className="w-full border border-purple-200 rounded-xl px-3 py-2 text-sm text-gray-700 resize-none h-20 focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white mb-3"
                        autoFocus
                    />
                    <div className="flex gap-2">
                        <button onClick={() => { setEditMode(false); setEditPrompt(''); }}
                            className="flex-1 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-500 hover:bg-gray-50 transition">
                            Cancel
                        </button>
                        <button onClick={handleEdit} disabled={!editPrompt.trim() || editing}
                            className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl text-xs font-bold disabled:opacity-40 flex items-center justify-center gap-1.5 hover:opacity-90 transition">
                            {editing ? <><Loader2 size={12} className="animate-spin" /> Editing…</> : <><Wand2 size={12} /> Apply Edit</>}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── A not-yet-generated slot: generate now, or preview/edit the prompt first ─
const JobSlot = ({ job, isLoading, error, previewPrompt, previewLoading, onPreview, onGenerate, onCustomPromptChange, onRemoveCustom }) => {
    const [showEditor, setShowEditor] = useState(false);
    const [draft, setDraft] = useState('');
    const Icon = job.icon;

    const openEditor = async () => {
        if (job.isCustom) {
            setDraft(job.customPrompt || '');
            setShowEditor(true);
            return;
        }
        if (!previewPrompt) await onPreview(job);
        setDraft(previewPrompt || '');
        setShowEditor(true);
    };

    // keep draft synced once a preview prompt arrives after opening
    React.useEffect(() => {
        if (showEditor && !job.isCustom && previewPrompt && !draft) setDraft(previewPrompt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewPrompt]);

    if (isLoading) return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden animate-fadeIn">
            <div className="aspect-square bg-gradient-to-br from-purple-100 to-pink-100 flex flex-col items-center justify-center gap-3 p-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg animate-pulse">
                    <Icon size={22} className="text-white" />
                </div>
                <p className="font-bold text-gray-700 text-sm">{job.label}</p>
                <div className="flex gap-1.5">
                    {[0, 1, 2].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}
                </div>
            </div>
        </div>
    );

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden animate-fadeIn">
            <div className={`p-5 flex flex-col gap-3 ${!showEditor ? 'items-center text-center' : ''}`}>
                {!showEditor && (
                    <>
                        <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center">
                            <Icon size={22} className="text-purple-500" />
                        </div>
                        <div>
                            <p className="font-bold text-gray-800 text-sm">{job.label}</p>
                            {job.desc && <p className="text-[11px] text-gray-400 mt-0.5">{job.desc}</p>}
                        </div>
                        {error && <p className="text-xs text-red-500">{error}</p>}
                        <div className="flex gap-2 w-full mt-1">
                            {job.isCustom ? (
                                <button onClick={openEditor}
                                    className="flex-1 py-2 rounded-xl text-xs font-bold border-2 border-purple-300 text-purple-600 hover:bg-purple-50 transition">
                                    Write prompt
                                </button>
                            ) : (
                                <>
                                    <button onClick={openEditor} disabled={previewLoading}
                                        className="flex-1 py-2 rounded-xl text-xs font-bold border-2 border-purple-300 text-purple-600 hover:bg-purple-50 transition disabled:opacity-50 flex items-center justify-center gap-1">
                                        {previewLoading ? <Loader2 size={12} className="animate-spin" /> : <Edit3 size={12} />} Edit prompt
                                    </button>
                                    <button onClick={() => onGenerate(job, null)}
                                        className="flex-1 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:opacity-90 transition">
                                        Generate
                                    </button>
                                </>
                            )}
                        </div>
                        {job.isCustom && (
                            <button onClick={() => onRemoveCustom(job.id)} className="text-[11px] text-gray-400 hover:text-red-500 transition flex items-center gap-1">
                                <Trash2 size={11} /> Remove
                            </button>
                        )}
                    </>
                )}

                {showEditor && (
                    <div className="w-full">
                        <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                            <Icon size={13} className="text-purple-500" /> {job.label}
                        </p>
                        <textarea
                            value={draft}
                            onChange={e => {
                                setDraft(e.target.value);
                                if (job.isCustom) onCustomPromptChange(job.id, e.target.value);
                            }}
                            placeholder={job.isCustom ? "Describe exactly the image you want…" : "Loading suggested prompt…"}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-700 resize-none h-28 focus:outline-none focus:ring-2 focus:ring-purple-400"
                            autoFocus
                        />
                        {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
                        <div className="flex gap-2 mt-3">
                            <button onClick={() => setShowEditor(false)}
                                className="flex-1 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-500 hover:bg-gray-50 transition">
                                Cancel
                            </button>
                            <button onClick={() => { onGenerate(job, draft); setShowEditor(false); }} disabled={!draft.trim()}
                                className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl text-xs font-bold disabled:opacity-40 flex items-center justify-center gap-1.5 hover:opacity-90 transition">
                                <Sparkles size={12} /> Generate
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const BrandImagesPage = ({ user }) => {
    const [step, setStep] = useState(1);

    // Input mode toggle
    const [inputMode, setInputMode] = useState('asin'); // 'asin' | 'upload'

    // Step 1 — product (ASIN/UPC mode)
    const [asin, setAsin] = useState('');
    const [loading, setLoading] = useState(false);
    const [product, setProduct] = useState(null);
    const [error, setError] = useState('');

    // Step 1 — upload mode (image + free-text context, no Keepa lookup)
    const [uploadedImage, setUploadedImage] = useState(null); // { dataUri, fileName }
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadContext, setUploadContext] = useState('');
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file.');
            return;
        }
        setError('');
        const reader = new FileReader();
        reader.onload = () => {
            setUploadedImage({ dataUri: reader.result, fileName: file.name });
        };
        reader.readAsDataURL(file);
    };

    // Step 2 — style selection + options
    const [selectedStyleIds, setSelectedStyleIds] = useState(new Set(DEFAULT_SELECTED));
    const [customJobs, setCustomJobs] = useState([]); // [{ id, label, customPrompt }]
    const [selectedSize, setSelectedSize] = useState(SIZES[1]);
    const [brandColor, setBrandColor] = useState('');
    const [customNotes, setCustomNotes] = useState('');

    const toggleStyle = (id) => {
        setSelectedStyleIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const addCustomJob = () => {
        const id = `custom-${Date.now()}`;
        setCustomJobs(prev => [...prev, { id, label: `Custom ${prev.length + 1}`, customPrompt: '' }]);
    };
    const updateCustomJobPrompt = (id, text) => {
        setCustomJobs(prev => prev.map(c => c.id === id ? { ...c, customPrompt: text } : c));
    };
    const removeCustomJob = (id) => {
        setCustomJobs(prev => prev.filter(c => c.id !== id));
    };

    // Every job the user has queued up for step 3 — real styles + custom ones
    const activeJobs = [
        ...STYLES.filter(s => selectedStyleIds.has(s.id)),
        ...customJobs.map(c => ({ id: c.id, label: c.label, icon: MessageSquarePlus, desc: 'Custom prompt', isCustom: true, customPrompt: c.customPrompt })),
    ];

    const getOrderIndex = (id) => {
        const styleIdx = STYLES.findIndex(s => s.id === id);
        if (styleIdx >= 0) return styleIdx;
        const customIdx = customJobs.findIndex(c => c.id === id);
        return STYLES.length + (customIdx >= 0 ? customIdx : 0);
    };

    // Step 3 — results
    const [generatingStyles, setGeneratingStyles] = useState(new Set());
    const [generatedImages, setGeneratedImages] = useState([]);
    const [referenceImage, setReferenceImage] = useState(null);
    const [genErrors, setGenErrors] = useState({});
    const [previewPrompts, setPreviewPrompts] = useState({});
    const [previewLoading, setPreviewLoading] = useState(new Set());

    // Quick Variation — supports uploading several variants at once (e.g. all
    // the colors in a product line), each gets the full set of shots recreated.
    const [variations, setVariations] = useState([]); // [{ id, dataUri, fileName, label }]
    const [variationResults, setVariationResults] = useState({}); // { "variantId:styleId": imgData }
    const [variationGenerating, setVariationGenerating] = useState(new Set()); // "variantId:styleId"
    const [variationErrors, setVariationErrors] = useState({}); // { "variantId:styleId": err }
    const variationFileRef = useRef(null);
    const variationKey = (variantId, styleId) => `${variantId}:${styleId}`;

    // ── Step 1 handlers ───────────────────────────────────────────────────────
    const fetchProduct = async () => {
        if (!asin.trim()) return;
        setLoading(true);
        setError('');
        setProduct(null);
        try {
            const r = await authService.authenticatedFetch(`${API_BASE}/api/ebay/keepa-product-data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ identifier: asin.trim() }),
            });
            const data = await r.json();
            if (data.error) throw new Error(data.error);
            setProduct(data);
        } catch (e) {
            setError(e.message || 'Failed to fetch product.');
        } finally {
            setLoading(false);
        }
    };

    const canProceed = inputMode === 'asin' ? !!product : !!uploadedImage;

    // ── Prompt preview (for "Edit prompt" before generating) ─────────────────
    const buildPromptRequest = useCallback((styleId) => {
        const promptEndpoint = inputMode === 'upload'
            ? `${API_BASE}/api/amazon/brand-images/generate-prompt-from-upload`
            : `${API_BASE}/api/amazon/brand-images/generate-prompt`;

        const promptBody = inputMode === 'upload'
            ? {
                title: uploadTitle || uploadedImage?.fileName || 'product',
                context: uploadContext,
                style: styleId,
                brand_color: brandColor,
            }
            : {
                title: product?.title || asin || 'product',
                description: product?.description || '',
                bullets: product?.bullets || [],
                style: styleId,
                brand_color: brandColor,
                custom_notes: customNotes,
            };
        return { promptEndpoint, promptBody };
    }, [inputMode, uploadTitle, uploadedImage, uploadContext, brandColor, product, asin, customNotes]);

    const previewPromptForJob = useCallback(async (job) => {
        setPreviewLoading(prev => new Set([...prev, job.id]));
        setGenErrors(prev => ({ ...prev, [job.id]: null }));
        try {
            const { promptEndpoint, promptBody } = buildPromptRequest(job.id);
            const pr = await authService.authenticatedFetch(promptEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(promptBody),
            });
            const data = await pr.json();
            if (data.error) throw new Error(data.error);
            setPreviewPrompts(prev => ({ ...prev, [job.id]: withHeroBackground(data.prompt, job.id) }));
        } catch (e) {
            setGenErrors(prev => ({ ...prev, [job.id]: e.message || 'Could not load prompt' }));
        } finally {
            setPreviewLoading(prev => { const n = new Set(prev); n.delete(job.id); return n; });
        }
    }, [buildPromptRequest]);

    // ── Step 3: generation ────────────────────────────────────────────────────
    const generateForStyle = useCallback(async (styleId, overridePrompt = null) => {
        setGeneratingStyles(prev => new Set([...prev, styleId]));
        setGenErrors(prev => ({ ...prev, [styleId]: null }));

        try {
            let promptData = null;
            let finalPrompt = overridePrompt;

            if (!overridePrompt) {
                const { promptEndpoint, promptBody } = buildPromptRequest(styleId);
                const pr = await authService.authenticatedFetch(promptEndpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(promptBody),
                });
                promptData = await pr.json();
                if (promptData.error) throw new Error(promptData.error);
                finalPrompt = promptData.prompt;
            }

            finalPrompt = withHeroBackground(finalPrompt, styleId);

            let gData;
            if (inputMode === 'upload') {
                const activeReference = referenceImage?.image_url || null;
                const gr = await authService.authenticatedFetch(`${API_BASE}/api/amazon/brand-images/generate-from-upload`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        prompt: finalPrompt,
                        width: selectedSize.w,
                        height: selectedSize.h,
                        image_data: activeReference || uploadedImage?.dataUri,
                        quality: qualityFor(styleId),
                    }),
                });
                gData = await gr.json();
            } else {
                const productImageUrl = product?.image_urls?.[0] || null;
                const activeReference = referenceImage?.image_url || productImageUrl || null;
                const gr = await authService.authenticatedFetch(`${API_BASE}/api/amazon/brand-images/generate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        prompt: finalPrompt,
                        width: selectedSize.w,
                        height: selectedSize.h,
                        reference_image_url: activeReference,
                        quality: qualityFor(styleId),
                    }),
                });
                gData = await gr.json();
            }
            if (gData.error) throw new Error(gData.error);

            setGeneratedImages(prev => {
                const newImg = { ...promptData, ...gData, style_name: styleId, prompt: finalPrompt, timestamp: Date.now() };
                const updated = prev.filter(img => img.style_name !== styleId);
                return [...updated, newImg].sort((a, b) => getOrderIndex(a.style_name) - getOrderIndex(b.style_name));
            });

        } catch (e) {
            setGenErrors(prev => ({ ...prev, [styleId]: e.message || 'Failed' }));
        } finally {
            setGeneratingStyles(prev => { const next = new Set(prev); next.delete(styleId); return next; });
        }
    }, [product, selectedSize, referenceImage, inputMode, uploadedImage, buildPromptRequest, customJobs]);

    const handleGenerateJob = (job, overridePrompt) => {
        if (job.isCustom) {
            const prompt = (overridePrompt ?? job.customPrompt ?? '').trim();
            if (!prompt) {
                setGenErrors(prev => ({ ...prev, [job.id]: 'Write a prompt first.' }));
                return;
            }
            generateForStyle(job.id, prompt);
        } else {
            generateForStyle(job.id, overridePrompt || null);
        }
    };

    const handleDownload = async (img, suffix = '') => {
        try {
            const r = await fetch(img.image_url);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(product?.title || uploadTitle || 'product').slice(0, 30)}-${img.style_name}${suffix}.png`;
            a.click();
        } catch (_) { }
    };

    const handleGenerateSelected = () => {
        activeJobs.forEach(job => handleGenerateJob(job));
    };

    const handleEdit = useCallback(async (img, editInstruction) => {
        const editedPrompt = withHeroBackground(
            `${img.prompt || ''}\n\nEDIT INSTRUCTION: ${editInstruction}. Keep the product packaging completely unchanged. Only modify the scene/background/props as instructed.`,
            img.style_name
        );

        setGeneratingStyles(prev => new Set([...prev, img.style_name]));
        setGenErrors(prev => ({ ...prev, [img.style_name]: null }));

        try {
            const gr = await authService.authenticatedFetch(`${API_BASE}/api/amazon/brand-images/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt: editedPrompt,
                    width: selectedSize.w,
                    height: selectedSize.h,
                    reference_image_url: img.image_url,
                    quality: qualityFor(img.style_name),
                }),
            });
            const gData = await gr.json();
            if (gData.error) throw new Error(gData.error);

            setGeneratedImages(prev => {
                const updated = prev.filter(i => i.style_name !== img.style_name);
                return [...updated, { ...img, ...gData, prompt: editedPrompt, timestamp: Date.now() }]
                    .sort((a, b) => getOrderIndex(a.style_name) - getOrderIndex(b.style_name));
            });
        } catch (e) {
            setGenErrors(prev => ({ ...prev, [img.style_name]: e.message || 'Edit failed' }));
        } finally {
            setGeneratingStyles(prev => { const next = new Set(prev); next.delete(img.style_name); return next; });
        }
    }, [selectedSize]);

    // ── Quick Variation ───────────────────────────────────────────────────────
    // Lets the user upload several variants at once (e.g. every color in a
    // line) and recreate the full set of already-generated shots for each one.
    const handleVariationFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        files.forEach(file => {
            if (!file.type.startsWith('image/')) return;
            const reader = new FileReader();
            reader.onload = () => {
                setVariations(prev => [...prev, {
                    id: `variant-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                    dataUri: reader.result,
                    fileName: file.name,
                    label: '',
                }]);
            };
            reader.readAsDataURL(file);
        });
        if (variationFileRef.current) variationFileRef.current.value = '';
    };

    const updateVariationLabel = (id, label) => {
        setVariations(prev => prev.map(v => v.id === id ? { ...v, label } : v));
    };

    const removeVariation = (id) => {
        setVariations(prev => prev.filter(v => v.id !== id));
        setVariationResults(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(k => { if (k.startsWith(`${id}:`)) delete next[k]; });
            return next;
        });
    };

    const generateVariationForImage = useCallback(async (variant, img) => {
        const key = variationKey(variant.id, img.style_name);
        setVariationGenerating(prev => new Set([...prev, key]));
        setVariationErrors(prev => ({ ...prev, [key]: null }));
        try {
            const prompt = withHeroBackground(img.prompt, img.style_name);
            // Two labeled reference images — Image 1 is the new variant
            // (copy its color/packaging), Image 2 is the original shot
            // (preserve its exact composition/background/lighting). The
            // backend prepends the role labels to the prompt.
            const gr = await authService.authenticatedFetch(`${API_BASE}/api/amazon/brand-images/generate-from-upload`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt,
                    width: selectedSize.w,
                    height: selectedSize.h,
                    quality: qualityFor(img.style_name),
                    images: [
                        { data: variant.dataUri, role: 'new product variant to use — copy its exact color, packaging, and label' },
                        { data: img.image_url, role: 'scene and composition to preserve exactly — background, lighting, camera angle, and prop placement' },
                    ],
                }),
            });
            const gData = await gr.json();
            if (gData.error) throw new Error(gData.error);
            setVariationResults(prev => ({ ...prev, [key]: { ...gData, style_name: img.style_name, prompt, timestamp: Date.now() } }));
        } catch (e) {
            setVariationErrors(prev => ({ ...prev, [key]: e.message || 'Failed' }));
        } finally {
            setVariationGenerating(prev => { const n = new Set(prev); n.delete(key); return n; });
        }
    }, [selectedSize]);

    const generateAllVariations = () => {
        const targets = generatedImages.filter(img => img.image_url);
        variations.forEach(variant => targets.forEach(img => generateVariationForImage(variant, img)));
    };

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 pb-16">
            <style>{`
                @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
                .animate-fadeIn { animation: fadeIn .35s ease both; }
            `}</style>

            <div className="max-w-5xl mx-auto px-4 pt-10">

                <div className="mb-8 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-xl">
                        <Wand2 className="text-white" size={24} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900">Product Photo Studio</h1>
                        <p className="text-sm text-gray-500">Generate stunning Amazon listing images</p>
                    </div>
                </div>

                <Steps current={step} />

                {/* ── STEP 1: Product ── */}
                {step === 1 && (
                    <div className="animate-fadeIn">
                        <div className="flex gap-2 mb-5 bg-gray-100 p-1.5 rounded-2xl w-fit">
                            <button onClick={() => setInputMode('asin')}
                                className={`px-5 py-2 rounded-xl text-sm font-semibold transition ${inputMode === 'asin' ? 'bg-white text-purple-600 shadow' : 'text-gray-500 hover:text-gray-700'}`}>
                                Look Up by ASIN
                            </button>
                            <button onClick={() => setInputMode('upload')}
                                className={`px-5 py-2 rounded-xl text-sm font-semibold transition ${inputMode === 'upload' ? 'bg-white text-purple-600 shadow' : 'text-gray-500 hover:text-gray-700'}`}>
                                Upload Image + Context
                            </button>
                        </div>

                        {inputMode === 'asin' && (
                            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 mb-6">
                                <h2 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
                                    <Search size={20} className="text-purple-500" /> Find Your Product
                                </h2>
                                <p className="text-gray-500 text-sm mb-6">Enter an ASIN or UPC to load product info.</p>

                                <div className="flex gap-3 mb-4">
                                    <input
                                        value={asin}
                                        onChange={e => setAsin(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && fetchProduct()}
                                        placeholder="e.g. B08N5WRWNW or 012345678901"
                                        className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-400 transition"
                                    />
                                    <button onClick={fetchProduct} disabled={loading || !asin.trim()}
                                        className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition shadow-lg disabled:opacity-40 flex items-center gap-2">
                                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                                        Look Up
                                    </button>
                                </div>

                                {error && <p className="text-sm text-red-500 mb-4">⚠️ {error}</p>}
                            </div>
                        )}

                        {inputMode === 'upload' && (
                            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 mb-6">
                                <h2 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
                                    <ImageIcon size={20} className="text-purple-500" /> Upload Your Product Image
                                </h2>
                                <p className="text-gray-500 text-sm mb-6">Upload a photo of your product and tell us a bit about it — no ASIN needed.</p>

                                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
                                {!uploadedImage ? (
                                    <button onClick={() => fileInputRef.current?.click()}
                                        className="w-full border-2 border-dashed border-purple-200 rounded-2xl py-10 flex flex-col items-center gap-3 hover:border-purple-400 hover:bg-purple-50/50 transition">
                                        <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center">
                                            <ImageIcon size={22} className="text-purple-500" />
                                        </div>
                                        <p className="text-sm font-semibold text-gray-700">Click to upload a product photo</p>
                                        <p className="text-xs text-gray-400">PNG or JPG</p>
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-4 bg-purple-50 border border-purple-200 rounded-2xl p-4 mb-2">
                                        <img src={uploadedImage.dataUri} alt="" className="w-20 h-20 object-contain rounded-xl bg-white border border-purple-100" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-800 truncate">{uploadedImage.fileName}</p>
                                            <button onClick={() => { setUploadedImage(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                                                className="text-xs text-red-500 font-semibold hover:underline mt-1">
                                                Remove and choose another
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {error && <p className="text-sm text-red-500 mt-3 mb-2">⚠️ {error}</p>}

                                <div className="grid sm:grid-cols-2 gap-4 mt-5">
                                    <div>
                                        <label className="text-xs text-gray-500 font-semibold mb-1.5 block">Product Name</label>
                                        <input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)}
                                            placeholder="e.g. Lavender Honey Body Scrub"
                                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 font-semibold mb-1.5 block">Context for AI</label>
                                        <input value={uploadContext} onChange={e => setUploadContext(e.target.value)}
                                            placeholder="e.g. 'Natural skincare, made with honey and lavender, gift-ready packaging'"
                                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {product && (
                            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 mb-6 animate-fadeIn">
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Product Found</p>
                                <div className="flex gap-5">
                                    <div className="flex gap-2 flex-shrink-0">
                                        {product.image_urls?.slice(0, 4).map((url, i) => (
                                            <div key={i} className={`rounded-xl overflow-hidden border-2 bg-gray-50 ${i === 0 ? 'border-purple-300 w-28 h-28' : 'border-gray-100 w-14 h-14 hidden sm:block'}`}>
                                                <img src={url} alt="" className="w-full h-full object-contain p-1" />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-gray-900 leading-tight mb-2">{product.title}</p>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {product.asin && <span className="text-xs bg-purple-50 text-purple-600 border border-purple-200 px-2 py-0.5 rounded-full font-mono">{product.asin}</span>}
                                            {product.brand && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-semibold">{product.brand}</span>}
                                            {product.color && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{product.color}</span>}
                                        </div>
                                        {product.bullets?.length > 0 && (
                                            <ul className="space-y-1">
                                                {product.bullets.slice(0, 3).map((b, i) => (
                                                    <li key={i} className="flex items-start gap-1.5 text-xs text-gray-500">
                                                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
                                                        <span className="line-clamp-1">{b}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {canProceed && (
                            <button onClick={() => setStep(2)}
                                className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-2xl font-extrabold text-base hover:opacity-90 transition shadow-xl flex items-center justify-center gap-2 animate-fadeIn">
                                Choose Style <ChevronRight size={18} />
                            </button>
                        )}
                    </div>
                )}

                {/* ── STEP 2: Style selection + options ── */}
                {step === 2 && (
                    <div className="animate-fadeIn">
                        <button onClick={() => setStep(1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition font-medium mb-6">
                            <ArrowLeft size={16} /> Back
                        </button>

                        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 mb-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-1">Pick What To Generate</h2>
                            <p className="text-gray-500 text-sm mb-6">Select any styles you want. Not every style fits every product — leave the rest unchecked.</p>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                                {STYLES.map(s => {
                                    const Icon = s.icon;
                                    const selected = selectedStyleIds.has(s.id);
                                    return (
                                        <button key={s.id} onClick={() => toggleStyle(s.id)}
                                            className={`flex items-start gap-2 text-left border rounded-xl px-3 py-2.5 transition ${selected ? 'bg-purple-50 border-purple-300' : 'bg-gray-50 border-gray-100 hover:border-purple-200'}`}>
                                            {selected ? <CheckSquare size={15} className="text-purple-600 mt-0.5 flex-shrink-0" /> : <Square size={15} className="text-gray-300 mt-0.5 flex-shrink-0" />}
                                            <div className="min-w-0">
                                                <p className="text-xs font-semibold text-gray-700 leading-tight flex items-center gap-1">
                                                    <Icon size={12} className="text-purple-400" /> {s.label}
                                                </p>
                                                <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{s.desc}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Custom prompt slots */}
                            <div className="space-y-2 mb-2">
                                {customJobs.map(c => (
                                    <div key={c.id} className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-xl px-3 py-2">
                                        <MessageSquarePlus size={14} className="text-purple-500 flex-shrink-0" />
                                        <input value={c.customPrompt} onChange={e => updateCustomJobPrompt(c.id, e.target.value)}
                                            placeholder="Describe the custom image you want…"
                                            className="flex-1 bg-transparent text-xs text-gray-700 focus:outline-none" />
                                        <button onClick={() => removeCustomJob(c.id)} className="text-purple-300 hover:text-red-500 transition flex-shrink-0">
                                            <X size={13} />
                                        </button>
                                    </div>
                                ))}
                                <button onClick={addCustomJob}
                                    className="flex items-center gap-1.5 text-xs font-bold text-purple-600 hover:text-purple-800 transition">
                                    <Plus size={13} /> Add custom prompt
                                </button>
                            </div>
                        </div>

                        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 mb-6">
                            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Canvas Size</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                                {SIZES.map(s => (
                                    <button key={s.label} onClick={() => setSelectedSize(s)}
                                        className={`py-3 px-4 rounded-xl text-xs font-semibold border-2 transition ${selectedSize.label === s.label ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-100 text-gray-600 hover:border-purple-200'}`}>
                                        {s.label}<br /><span className="font-mono text-[10px] opacity-60">{s.w}×{s.h}</span>
                                    </button>
                                ))}
                            </div>

                            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Brand Hints <span className="text-gray-400 font-normal normal-case">(optional)</span></h3>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-gray-500 font-semibold mb-1.5 block">Brand Color</label>
                                    <div className="flex gap-2">
                                        <input type="color" value={brandColor || '#7c3aed'} onChange={e => setBrandColor(e.target.value)}
                                            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer flex-shrink-0" />
                                        <input value={brandColor} onChange={e => setBrandColor(e.target.value)}
                                            placeholder="#7c3aed or 'deep blue'"
                                            className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 font-semibold mb-1.5 block">Custom Notes for AI</label>
                                    <input value={customNotes} onChange={e => setCustomNotes(e.target.value)}
                                        placeholder="e.g. 'Show lemon slices, dark luxury background'"
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
                                </div>
                            </div>
                        </div>

                        <button onClick={() => setStep(3)} disabled={activeJobs.length === 0}
                            className="w-full py-5 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-2xl font-extrabold text-lg hover:opacity-90 transition shadow-xl flex items-center justify-center gap-3 disabled:opacity-40">
                            <Sparkles size={22} /> Continue {activeJobs.length > 0 ? `(${activeJobs.length} selected)` : ''}
                        </button>
                    </div>
                )}

                {/* ── STEP 3: Results ── */}
                {step === 3 && (
                    <div className="animate-fadeIn">
                        <div className="flex items-center gap-3 mb-6 flex-wrap">
                            <button onClick={() => setStep(2)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition font-medium">
                                <ArrowLeft size={16} /> Back
                            </button>

                            {inputMode === 'asin' && product && (
                                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow border border-gray-100 ml-1">
                                    {product.image_urls?.[0] && <img src={product.image_urls[0]} className="w-6 h-6 object-contain rounded" />}
                                    <span className="text-xs font-semibold text-gray-700 truncate max-w-[180px]">{product.title}</span>
                                </div>
                            )}
                            {inputMode === 'upload' && uploadedImage && (
                                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow border border-gray-100 ml-1">
                                    <img src={uploadedImage.dataUri} className="w-6 h-6 object-contain rounded" />
                                    <span className="text-xs font-semibold text-gray-700 truncate max-w-[180px]">{uploadTitle || uploadedImage.fileName}</span>
                                </div>
                            )}

                            {referenceImage && (
                                <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 px-3 py-1.5 rounded-full ml-auto">
                                    <img src={referenceImage.image_url} className="w-6 h-6 object-cover rounded" />
                                    <span className="text-xs font-bold text-purple-700">Reference active</span>
                                    <button onClick={() => setReferenceImage(null)} className="text-purple-400 hover:text-red-500 transition ml-1"><X size={12} /></button>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-3 mb-6">
                            <button onClick={handleGenerateSelected} disabled={generatingStyles.size > 0}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border-2 border-pink-300 text-pink-600 hover:bg-pink-50 transition disabled:opacity-40 bg-white">
                                <Sparkles size={11} /> Generate All Selected
                            </button>
                            <p className="text-xs text-gray-400 ml-auto">
                                {generatingStyles.size > 0 ? `Generating ${generatingStyles.size} image${generatingStyles.size > 1 ? 's' : ''}…` : `${generatedImages.length} / ${activeJobs.length} ready`}
                            </p>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                            {activeJobs.map(job => {
                                const img = generatedImages.find(i => i.style_name === job.id);
                                const isLoading = generatingStyles.has(job.id);
                                const err = genErrors[job.id];

                                if (img && !isLoading) return (
                                    <ImageCard
                                        key={job.id}
                                        img={img}
                                        meta={job}
                                        generating={isLoading}
                                        onRegenerate={i => generateForStyle(i.style_name)}
                                        onDownload={handleDownload}
                                        onEdit={handleEdit}
                                    />
                                );

                                return (
                                    <JobSlot
                                        key={job.id}
                                        job={job}
                                        isLoading={isLoading}
                                        error={err}
                                        previewPrompt={previewPrompts[job.id]}
                                        previewLoading={previewLoading.has(job.id)}
                                        onPreview={previewPromptForJob}
                                        onGenerate={handleGenerateJob}
                                        onCustomPromptChange={updateCustomJobPrompt}
                                        onRemoveCustom={removeCustomJob}
                                    />
                                );
                            })}
                        </div>

                        {/* ── Quick Variation ── */}
                        {generatedImages.length > 0 && (
                            <div className="mt-10 bg-white rounded-3xl shadow-xl border border-purple-100 p-8 animate-fadeIn">
                                <h2 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
                                    <Palette size={20} className="text-purple-500" /> Quick Variation
                                </h2>
                                <p className="text-gray-500 text-sm mb-6">
                                    Upload every other version of this product (colors, flavors, sizes…) and we'll recreate the same {generatedImages.length} shot{generatedImages.length > 1 ? 's' : ''} for each one.
                                </p>

                                <input ref={variationFileRef} type="file" accept="image/*" multiple onChange={handleVariationFileSelect} className="hidden" />

                                <button onClick={() => variationFileRef.current?.click()}
                                    className="w-full border-2 border-dashed border-purple-200 rounded-2xl py-6 flex flex-col items-center gap-2 hover:border-purple-400 hover:bg-purple-50/50 transition mb-4">
                                    <Upload size={18} className="text-purple-400" />
                                    <p className="text-sm font-semibold text-gray-700">
                                        {variations.length > 0 ? 'Add another variant' : 'Upload variant photos'}
                                    </p>
                                    <p className="text-xs text-gray-400">Select multiple at once, or add them one by one</p>
                                </button>

                                {variations.length > 0 && (
                                    <>
                                        <div className="space-y-2 mb-4">
                                            {variations.map(v => (
                                                <div key={v.id} className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-2xl p-3">
                                                    <img src={v.dataUri} alt="" className="w-12 h-12 object-contain rounded-lg bg-white border border-purple-100 flex-shrink-0" />
                                                    <input value={v.label} onChange={e => updateVariationLabel(v.id, e.target.value)}
                                                        placeholder={`Label, e.g. 'Blue' (${v.fileName})`}
                                                        className="flex-1 min-w-0 text-sm font-semibold text-gray-800 bg-transparent focus:outline-none border-b border-purple-200 pb-1" />
                                                    <button onClick={() => removeVariation(v.id)} className="text-purple-300 hover:text-red-500 transition flex-shrink-0">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>

                                        <button onClick={generateAllVariations} disabled={variationGenerating.size > 0}
                                            className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-40 flex items-center justify-center gap-2 mb-6">
                                            {variationGenerating.size > 0 ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                                            Generate {variations.length > 1 ? `All ${variations.length} Variation Sets` : 'Variation Set'}
                                        </button>

                                        {variations.map(variant => (
                                            <div key={variant.id} className="mb-8 last:mb-0">
                                                <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-3">
                                                    {variant.label || variant.fileName}
                                                </p>
                                                <div className="grid sm:grid-cols-2 gap-5">
                                                    {generatedImages.filter(i => i.image_url).map(orig => {
                                                        const key = variationKey(variant.id, orig.style_name);
                                                        const vimg = variationResults[key];
                                                        const vLoading = variationGenerating.has(key);
                                                        const vErr = variationErrors[key];
                                                        const meta = activeJobs.find(j => j.id === orig.style_name);
                                                        const Icon = meta?.icon || Palette;

                                                        if (vLoading) return (
                                                            <div key={key} className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden">
                                                                <div className="aspect-square flex flex-col items-center justify-center gap-2">
                                                                    <Loader2 size={20} className="text-purple-400 animate-spin" />
                                                                    <p className="text-xs font-semibold text-gray-500">{meta?.label}</p>
                                                                </div>
                                                            </div>
                                                        );

                                                        if (vErr) return (
                                                            <div key={key} className="bg-red-50 rounded-2xl border border-red-100 overflow-hidden">
                                                                <div className="aspect-square flex flex-col items-center justify-center gap-2 p-4 text-center">
                                                                    <Icon size={18} className="text-red-300" />
                                                                    <p className="text-xs font-semibold text-gray-600">{meta?.label}</p>
                                                                    <p className="text-[11px] text-red-500">{vErr}</p>
                                                                    <button onClick={() => generateVariationForImage(variant, orig)} className="text-xs font-bold text-purple-600 underline">Try again</button>
                                                                </div>
                                                            </div>
                                                        );

                                                        if (!vimg) return (
                                                            <div key={key} className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden">
                                                                <div className="aspect-square flex flex-col items-center justify-center gap-2">
                                                                    <Icon size={18} className="text-gray-300" />
                                                                    <p className="text-xs font-semibold text-gray-400">{meta?.label}</p>
                                                                    <button onClick={() => generateVariationForImage(variant, orig)}
                                                                        className="text-[11px] font-bold text-purple-500 underline hover:text-purple-700 transition">
                                                                        Generate
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );

                                                        return (
                                                            <div key={key} className="bg-white rounded-2xl shadow border border-gray-100 overflow-hidden group relative animate-fadeIn">
                                                                <img src={vimg.image_url} alt={meta?.label} className="w-full object-cover" />
                                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
                                                                    <button onClick={() => handleDownload(vimg, variant.label ? `-${variant.label}` : '-variation')}
                                                                        className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition">
                                                                        <Download size={16} className="text-gray-800" />
                                                                    </button>
                                                                    <button onClick={() => generateVariationForImage(variant, orig)}
                                                                        className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition">
                                                                        <RefreshCw size={16} className="text-gray-800" />
                                                                    </button>
                                                                </div>
                                                                <div className="absolute top-2 left-2">
                                                                    <span className="bg-black/60 text-white text-[10px] font-bold px-2 py-1 rounded-full">{meta?.label}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BrandImagesPage;